import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import usePracticeCourseToc from './usePracticeCourseToc';
import { TOC_STATUS } from '../../../types/courseToc.constants';
import { stripHtml } from 'core/utils/utils';

import type { TocLessonItem, TocUnitItem } from '../../../types/courseToc';
import type { PracticeLessonSelection } from '../../../types/practiceCourses';

export type UnitCheckState = 'checked' | 'indeterminate' | 'unchecked';

/** A lesson decorated with whether it can be practised (has KCs) or is disabled. */
export type DecoratedLesson = {
  lesson: TocLessonItem;
  /** A lesson with no practicable content (no KCs) is shown disabled. */
  isDisabled: boolean;
};

/** A unit decorated with the FE-derived view-model the dialog renders. */
export type DecoratedUnit = {
  unit: TocUnitItem;
  /** Lessons after applying the search filter (all lessons when no query). */
  lessons: DecoratedLesson[];
  /** A unit with no lessons but unit-direct KCs is selectable by its own id. */
  isModulesOnly: boolean;
  /** A unit with no practicable content (no KCs in itself nor its lessons). */
  isDisabled: boolean;
  isExpanded: boolean;
  checkState: UnitCheckState;
  /** Mastered lessons over total (student progress, NOT the selected count). */
  masteredLessonCount: number;
  totalLessonCount: number;
  titlePlain: string;
};

type UsePracticeLessonSelectionDialogParams = {
  courseId: string;
  courseTitle?: string;
  isOpen: boolean;
  onConfirm: (selection: PracticeLessonSelection) => void;
};

type UsePracticeLessonSelectionDialogResult = {
  decoratedUnits: DecoratedUnit[];
  handleClear: () => void;
  handleConfirm: () => void;
  handleSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleClearSearch: () => void;
  handleSelectAll: () => void;
  handleToggleLesson: (lessonId: string) => void;
  handleToggleUnit: (unit: TocUnitItem) => void;
  handleToggleUnitExpansion: (unitId: string) => void;
  hasError: boolean;
  hasPracticableContent: boolean;
  hasUnits: boolean;
  isConfirmDisabled: boolean;
  isLessonSelected: (lessonId: string) => boolean;
  isLoading: boolean;
  searchQuery: string;
  selectedCount: number;
  shouldShowPriorUnitsWarning: boolean;
  subtitle: string;
  t: (key: string, options?: Record<string, unknown>) => string;
};

/**
 * Selectable id of a unit that has no lessons (modules-only): its own unitId.
 * Uses `kcCount` (the same source as practicability) rather than `kcs.length`,
 * so a no-lessons unit is "modules-only" iff it is practicable — keeping
 * `isModulesOnlyUnit` and `isUnitPracticable` aligned and avoiding a selectable
 * unit whose own id is not selectable.
 */
function isModulesOnlyUnit(unit: TocUnitItem): boolean {
  return unit.lessons.length === 0 && unit.kcCount > 0;
}

/** A lesson is practicable when it carries at least one KC. */
function isLessonPracticable(lesson: TocLessonItem): boolean {
  return lesson.kcCount > 0;
}

/**
 * A unit is practicable when its aggregate KC count is positive. `kcCount`
 * already sums the unit-direct KCs plus every lesson's KCs, so it covers the
 * "neither the unit nor any of its lessons has KCs" criterion.
 */
function isUnitPracticable(unit: TocUnitItem): boolean {
  return unit.kcCount > 0;
}

/**
 * Logic for the practice lesson selection dialog (popup 2, TANAI-1869). Fetches
 * the course ToC WITH the student's progress, drives the tri-state selection,
 * search filtering, select-all/clear, the "prior units not practised" warning,
 * and builds the `{ lessonIds, unitIds }` payload on confirm.
 */
function usePracticeLessonSelectionDialog({
  courseId,
  courseTitle,
  isOpen,
  onConfirm,
}: UsePracticeLessonSelectionDialogParams): UsePracticeLessonSelectionDialogResult {
  const { t } = useTranslation(['chatbot']);

  // Session-scoped ToC for the logged-in student (user resolved from the token).
  // Only fetch while the dialog is open to avoid eager network calls.
  const { data, error, isLoading } = usePracticeCourseToc(isOpen ? courseId : null);

  const [selectedLessonIds, setSelectedLessonIds] = useState<Set<string>>(new Set());
  const [expandedUnitIds, setExpandedUnitIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Reset transient state whenever the dialog opens or closes.
  useEffect(() => {
    setSelectedLessonIds(new Set());
    setExpandedUnitIds(new Set());
    setSearchQuery('');
  }, [isOpen]);

  const units = useMemo<TocUnitItem[]>(() => data?.units ?? [], [data]);

  // Selectable id of a unit: its PRACTICABLE lessons' ids, or its own id when
  // modules-only (and practicable). Non-practicable content is never selectable,
  // so a parent unit toggle or select-all cannot sneak it into the selection.
  const getUnitLessonIds = useCallback((unit: TocUnitItem): string[] => {
    if (isModulesOnlyUnit(unit)) return isUnitPracticable(unit) ? [unit.unitId] : [];
    return unit.lessons.filter(isLessonPracticable).map((lesson) => lesson.lessonId);
  }, []);

  // Filter units/lessons by query: match on unit title OR any lesson title;
  // a lesson-only match auto-expands the unit.
  const filteredUnits = useMemo<{ unit: TocUnitItem; lessons: TocLessonItem[]; autoExpand: boolean }[]>(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) {
      return units.map((unit) => ({ unit, lessons: unit.lessons, autoExpand: false }));
    }

    return units
      .map((unit) => {
        const unitMatches = stripHtml(unit.title).toLowerCase().includes(query);
        const matchingLessons = unit.lessons.filter((lesson) => stripHtml(lesson.title).toLowerCase().includes(query));

        if (unitMatches || matchingLessons.length > 0) {
          return {
            unit,
            lessons: unitMatches ? unit.lessons : matchingLessons,
            autoExpand: matchingLessons.length > 0 && !unitMatches,
          };
        }
        return null;
      })
      .filter((entry): entry is { unit: TocUnitItem; lessons: TocLessonItem[]; autoExpand: boolean } => entry !== null);
  }, [units, searchQuery]);

  // Auto-expand units with lesson matches while a query is active.
  useEffect(() => {
    if (!searchQuery.trim()) return;
    setExpandedUnitIds((prev) => {
      const next = new Set(prev);
      filteredUnits.forEach((entry) => {
        if (entry.autoExpand) next.add(entry.unit.unitId);
      });
      return next;
    });
  }, [filteredUnits, searchQuery]);

  const getUnitCheckState = useCallback(
    (unit: TocUnitItem): UnitCheckState => {
      const ids = getUnitLessonIds(unit);
      if (ids.length === 0) return 'unchecked';
      const selectedCount = ids.filter((id) => selectedLessonIds.has(id)).length;
      if (selectedCount === 0) return 'unchecked';
      if (selectedCount === ids.length) return 'checked';
      return 'indeterminate';
    },
    [getUnitLessonIds, selectedLessonIds]
  );

  const decoratedUnits = useMemo<DecoratedUnit[]>(
    () =>
      filteredUnits.map((entry) => {
        const { unit } = entry;
        const modulesOnly = isModulesOnlyUnit(unit);
        const totalLessonCount = unit.lessonCount || unit.lessons.length;
        const masteredLessonCount = unit.lessons.filter((lesson) => lesson.lessonStatus === TOC_STATUS.MASTERED).length;

        return {
          unit,
          lessons: entry.lessons.map((lesson) => ({ lesson, isDisabled: !isLessonPracticable(lesson) })),
          isModulesOnly: modulesOnly,
          isDisabled: !isUnitPracticable(unit),
          isExpanded: expandedUnitIds.has(unit.unitId),
          checkState: getUnitCheckState(unit),
          masteredLessonCount,
          totalLessonCount,
          titlePlain: stripHtml(unit.title),
        };
      }),
    [filteredUnits, expandedUnitIds, getUnitCheckState]
  );

  const handleToggleUnitExpansion = useCallback((unitId: string) => {
    setExpandedUnitIds((prev) => {
      const next = new Set(prev);
      if (next.has(unitId)) {
        next.delete(unitId);
      } else {
        next.add(unitId);
      }
      return next;
    });
  }, []);

  const handleToggleLesson = useCallback(
    (lessonId: string) => {
      // Guard: a non-practicable lesson (no KCs) is disabled and cannot toggle.
      const lesson = units.flatMap((unit) => unit.lessons).find((item) => item.lessonId === lessonId);
      if (lesson && !isLessonPracticable(lesson)) return;
      setSelectedLessonIds((prev) => {
        const next = new Set(prev);
        if (next.has(lessonId)) {
          next.delete(lessonId);
        } else {
          next.add(lessonId);
        }
        return next;
      });
    },
    [units]
  );

  const handleToggleUnit = useCallback(
    (unit: TocUnitItem) => {
      const ids = getUnitLessonIds(unit);
      if (ids.length === 0) return;
      const allSelected = ids.every((id) => selectedLessonIds.has(id));
      setSelectedLessonIds((prev) => {
        const next = new Set(prev);
        if (allSelected) {
          ids.forEach((id) => next.delete(id));
        } else {
          ids.forEach((id) => next.add(id));
        }
        return next;
      });
    },
    [getUnitLessonIds, selectedLessonIds]
  );

  // Select all selectable ids across the FILTERED units, keeping any prior
  // selection (so selecting-all with an active search does not drop hidden picks).
  const handleSelectAll = useCallback(() => {
    setSelectedLessonIds((prev) => {
      const next = new Set(prev);
      filteredUnits.forEach((entry) => {
        getUnitLessonIds(entry.unit).forEach((id) => next.add(id));
      });
      return next;
    });
  }, [filteredUnits, getUnitLessonIds]);

  const handleClear = useCallback(() => {
    setSelectedLessonIds(new Set());
  }, []);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  const isLessonSelected = useCallback((lessonId: string) => selectedLessonIds.has(lessonId), [selectedLessonIds]);

  const handleConfirm = useCallback(() => {
    const lessonIds: string[] = [];
    const unitIds: string[] = [];

    units.forEach((unit) => {
      // Modules-only unit (no lessons): the unit itself is the selectable item.
      if (isModulesOnlyUnit(unit)) {
        if (selectedLessonIds.has(unit.unitId)) {
          unitIds.push(unit.unitId);
        }
        return;
      }
      // Only practicable lessons are selectable; non-practicable ones can never
      // be in the selection, so compare against the practicable lessons only.
      const practicableLessons = unit.lessons.filter(isLessonPracticable);
      const selectedUnitLessons = practicableLessons.filter((lesson) => selectedLessonIds.has(lesson.lessonId));
      if (selectedUnitLessons.length === 0) return;
      // All practicable lessons selected → send only the unit (not its lessons).
      if (selectedUnitLessons.length === practicableLessons.length) {
        unitIds.push(unit.unitId);
        return;
      }
      // Partial selection → send only the selected lessons (not the unit).
      selectedUnitLessons.forEach((lesson) => lessonIds.push(lesson.lessonId));
    });

    onConfirm({ lessonIds, unitIds });
  }, [onConfirm, selectedLessonIds, units]);

  const selectedCount = selectedLessonIds.size;
  const hasUnits = units.length > 0;
  const hasPracticableContent = units.some(isUnitPracticable);
  const hasError = Boolean(error);
  const isConfirmDisabled = selectedCount === 0;

  // "Nombre del programa – nombre de la clase": the program title comes from the
  // ToC (falling back to the prop); there is no class name in this read-model, so
  // we show just the program title when a class is not available.
  const subtitle = data?.title || courseTitle || '';

  // Heuristic: warn when the student selects lessons from a unit while an EARLIER
  // unit (lower `order`) is still NOT_STARTED. Compares against the smallest order
  // among units that have a selected lesson.
  const shouldShowPriorUnitsWarning = useMemo<boolean>(() => {
    if (selectedCount === 0) return false;

    const selectedUnitOrders = units
      .filter((unit) => {
        if (isModulesOnlyUnit(unit)) return selectedLessonIds.has(unit.unitId);
        return unit.lessons.some((lesson) => selectedLessonIds.has(lesson.lessonId));
      })
      .map((unit) => unit.order);

    if (selectedUnitOrders.length === 0) return false;
    const minSelectedOrder = Math.min(...selectedUnitOrders);

    return units.some((unit) => unit.order < minSelectedOrder && unit.unitStatus === TOC_STATUS.NOT_STARTED);
  }, [selectedCount, selectedLessonIds, units]);

  return {
    decoratedUnits,
    handleClear,
    handleConfirm,
    handleSearchChange,
    handleClearSearch,
    handleSelectAll,
    handleToggleLesson,
    handleToggleUnit,
    handleToggleUnitExpansion,
    hasError,
    hasPracticableContent,
    hasUnits,
    isConfirmDisabled,
    isLessonSelected,
    isLoading,
    searchQuery,
    selectedCount,
    shouldShowPriorUnitsWarning,
    subtitle,
    t,
  };
}

export default usePracticeLessonSelectionDialog;
